import { useEffect, useRef, useState } from "react";
import {
  Plus,
  Pencil,
  Trash2,
  ArrowUp,
  ArrowDown,
  Upload,
  Newspaper,
  ExternalLink,
  X,
} from "lucide-react";
import {
  fetchAdminPressArticles,
  createPressArticle,
  updatePressArticle,
  deletePressArticle,
  reorderPressArticles,
  uploadPressImage,
} from "@/lib/api";

const EMPTY = { publication: "", title: "", url: "", image: "", date: "" };

export default function PressManager({ password }) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // article object or {} for new
  const [deleteId, setDeleteId] = useState(null);
  const [reordering, setReordering] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      setArticles(await fetchAdminPressArticles(password));
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load articles.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const move = async (index, dir) => {
    const next = [...articles];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setArticles(next);
    setReordering(true);
    try {
      await reorderPressArticles(password, next.map((a) => a.id));
    } catch {
      setError("Failed to reorder. Reloading.");
      load();
    } finally {
      setReordering(false);
    }
  };

  const onDelete = async () => {
    try {
      await deletePressArticle(password, deleteId);
      setArticles((prev) => prev.filter((a) => a.id !== deleteId));
      setDeleteId(null);
    } catch {
      setError("Failed to delete article.");
    }
  };

  const onSaved = (saved, isNew) => {
    setArticles((prev) =>
      isNew ? [...prev, saved] : prev.map((a) => (a.id === saved.id ? saved : a))
    );
    setEditing(null);
  };

  return (
    <div data-testid="press-manager">
      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-fs-orange">
            Content
          </p>
          <h1 className="font-display text-3xl sm:text-4xl text-fs-cream">
            Press Articles{" "}
            <span className="text-fs-silver/60 text-2xl ml-1">
              ({articles.length})
            </span>
          </h1>
        </div>
        <button
          type="button"
          onClick={() => setEditing({ ...EMPTY })}
          className="btn-fs-primary"
          data-testid="press-add-btn"
        >
          <Plus className="h-4 w-4" />
          Add Article
        </button>
      </div>

      {error ? (
        <div
          className="mb-4 p-3 rounded-md border border-red-500/40 bg-red-500/10 text-sm text-red-300"
          data-testid="press-error"
        >
          {error}
        </div>
      ) : null}

      {loading ? (
        <p className="text-fs-silver p-8 text-center" data-testid="press-loading">
          Loading articles…
        </p>
      ) : articles.length === 0 ? (
        <div
          className="rounded-2xl border border-white/10 bg-fs-navy-2/60 p-12 text-center"
          data-testid="press-empty"
        >
          <Newspaper className="h-7 w-7 text-fs-silver/50 mx-auto mb-3" />
          <p className="text-fs-silver">
            No press articles yet. The "In the Press" section is hidden on the
            site until you add one.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {articles.map((a, i) => (
            <div
              key={a.id}
              className="rounded-2xl border border-white/10 bg-fs-navy-2/60 p-4 flex gap-4"
              data-testid={`press-item-${i}`}
            >
              <div className="h-20 w-28 shrink-0 rounded-lg bg-fs-navy-3 overflow-hidden flex items-center justify-center">
                {a.image ? (
                  <img
                    src={a.image}
                    alt={a.publication}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Newspaper className="h-6 w-6 text-fs-silver/30" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-xs uppercase tracking-[0.18em] text-fs-orange truncate">
                    {a.publication}
                  </p>
                  {a.date ? (
                    <span className="text-xs text-fs-silver/50 shrink-0">
                      · {a.date}
                    </span>
                  ) : null}
                </div>
                {a.title ? (
                  <p className="text-sm text-fs-cream mt-1 line-clamp-2">
                    {a.title}
                  </p>
                ) : null}
                {a.url ? (
                  <a
                    href={a.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-fs-gold hover:text-fs-orange inline-flex items-center gap-1 mt-1"
                  >
                    {a.url.replace(/^https?:\/\//, "").slice(0, 38)}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                ) : null}
              </div>
              <div className="flex flex-col items-end justify-between shrink-0">
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => setEditing(a)}
                    className="p-1.5 rounded-md text-fs-silver hover:text-fs-gold hover:bg-white/5"
                    aria-label="Edit"
                    data-testid={`press-edit-${i}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteId(a.id)}
                    className="p-1.5 rounded-md text-red-300 hover:text-red-200 hover:bg-white/5"
                    aria-label="Delete"
                    data-testid={`press-delete-${i}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="flex gap-1">
                  <button
                    type="button"
                    disabled={i === 0 || reordering}
                    onClick={() => move(i, -1)}
                    className="p-1.5 rounded-md text-fs-silver hover:text-fs-gold hover:bg-white/5 disabled:opacity-30"
                    aria-label="Move up"
                    data-testid={`press-up-${i}`}
                  >
                    <ArrowUp className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    disabled={i === articles.length - 1 || reordering}
                    onClick={() => move(i, 1)}
                    className="p-1.5 rounded-md text-fs-silver hover:text-fs-gold hover:bg-white/5 disabled:opacity-30"
                    aria-label="Move down"
                    data-testid={`press-down-${i}`}
                  >
                    <ArrowDown className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing ? (
        <ArticleForm
          password={password}
          article={editing}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
        />
      ) : null}

      {deleteId ? (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setDeleteId(null)}
          data-testid="press-delete-overlay"
        >
          <div
            className="w-full max-w-md rounded-2xl border border-red-500/30 bg-fs-navy-2 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-display text-2xl text-fs-cream">
              Delete this article?
            </h3>
            <p className="text-fs-silver text-sm mt-2">
              This permanently removes it from the site. This cannot be undone.
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                className="btn-fs-ghost"
                onClick={() => setDeleteId(null)}
                data-testid="press-delete-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-fs-primary !bg-none !text-white"
                style={{
                  background: "linear-gradient(180deg, #ef4444 0%, #b91c1c 100%)",
                }}
                onClick={onDelete}
                data-testid="press-delete-confirm"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ArticleForm({ password, article, onClose, onSaved }) {
  const isNew = !article.id;
  const [form, setForm] = useState({
    publication: article.publication || "",
    title: article.title || "",
    url: article.url || "",
    image: article.image || "",
    date: article.date || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setErr("");
    try {
      const url = await uploadPressImage(password, file);
      setForm((f) => ({ ...f, image: url }));
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Image upload failed.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.publication.trim()) {
      setErr("Publication name is required.");
      return;
    }
    setSaving(true);
    setErr("");
    try {
      const saved = isNew
        ? await createPressArticle(password, form)
        : await updatePressArticle(password, article.id, form);
      onSaved(saved, isNew);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Failed to save article.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="press-form-overlay"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={onSubmit}
        className="w-full max-w-lg my-8 rounded-2xl border border-white/10 bg-fs-navy-2 p-6 sm:p-8"
        data-testid="press-form"
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display text-2xl text-fs-cream">
            {isNew ? "Add Article" : "Edit Article"}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md text-fs-silver hover:text-fs-cream hover:bg-white/5"
            aria-label="Close"
            data-testid="press-form-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {err ? (
          <div className="mb-4 p-3 rounded-md border border-red-500/40 bg-red-500/10 text-sm text-red-300">
            {err}
          </div>
        ) : null}

        <div className="space-y-4">
          {/* Image */}
          <div>
            <span className="text-xs uppercase tracking-[0.2em] text-fs-silver">
              Image
            </span>
            <div className="mt-2 flex items-start gap-3">
              <div className="h-20 w-28 shrink-0 rounded-lg bg-fs-navy-3 overflow-hidden flex items-center justify-center border border-white/10">
                {form.image ? (
                  <img
                    src={form.image}
                    alt="preview"
                    className="w-full h-full object-cover"
                    data-testid="press-form-image-preview"
                  />
                ) : (
                  <Newspaper className="h-6 w-6 text-fs-silver/30" />
                )}
              </div>
              <div className="flex-1 space-y-2">
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="btn-fs-ghost !py-1.5 !px-3 text-sm w-full justify-center"
                  data-testid="press-form-upload-btn"
                >
                  <Upload className="h-4 w-4" />
                  {uploading ? "Uploading…" : "Upload image"}
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  onChange={onPickFile}
                  className="hidden"
                  data-testid="press-form-file-input"
                />
                <input
                  type="text"
                  value={form.image}
                  onChange={set("image")}
                  placeholder="…or paste an image URL"
                  className="fs-input text-sm"
                  data-testid="press-form-image-url"
                />
                {form.image ? (
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, image: "" }))}
                    className="text-xs text-fs-silver/70 hover:text-red-300"
                    data-testid="press-form-image-clear"
                  >
                    Remove image
                  </button>
                ) : null}
              </div>
            </div>
          </div>

          <label className="block">
            <span className="text-xs uppercase tracking-[0.2em] text-fs-silver">
              Publication name *
            </span>
            <input
              type="text"
              value={form.publication}
              onChange={set("publication")}
              placeholder="e.g. Variety"
              className="fs-input mt-1.5"
              data-testid="press-form-publication"
            />
          </label>

          <label className="block">
            <span className="text-xs uppercase tracking-[0.2em] text-fs-silver">
              Article title / headline
            </span>
            <input
              type="text"
              value={form.title}
              onChange={set("title")}
              placeholder="Optional headline"
              className="fs-input mt-1.5"
              data-testid="press-form-title"
            />
          </label>

          <label className="block">
            <span className="text-xs uppercase tracking-[0.2em] text-fs-silver">
              Link URL
            </span>
            <input
              type="url"
              value={form.url}
              onChange={set("url")}
              placeholder="https://…"
              className="fs-input mt-1.5"
              data-testid="press-form-url"
            />
          </label>

          <label className="block">
            <span className="text-xs uppercase tracking-[0.2em] text-fs-silver">
              Date
            </span>
            <input
              type="text"
              value={form.date}
              onChange={set("date")}
              placeholder="e.g. Jun 2026"
              className="fs-input mt-1.5"
              data-testid="press-form-date"
            />
          </label>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="btn-fs-ghost"
            data-testid="press-form-cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || uploading}
            className="btn-fs-primary"
            data-testid="press-form-save"
          >
            {saving ? "Saving…" : isNew ? "Add Article" : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
